import React from 'react';
import { ToolInfo, PdfImageModeInfo, ToolCategory } from '../types';
import { BLOG_POSTS } from './Blog';

interface SitemapProps {
    tools: ToolInfo[];
    pdfImageModes: PdfImageModeInfo[];
    navigateTo: (path: string) => void;
}

export const Sitemap: React.FC<SitemapProps> = ({ tools, pdfImageModes, navigateTo }) => {
    
    const allTools = [
        ...pdfImageModes,
        ...tools.filter(tool => tool.category && tool.category !== 'Site Pages'),
    ];

    const categoryOrder: string[] = [
        'Organize PDF', 'Optimize PDF', 'Convert to PDF', 'Convert from PDF', 'Edit PDF', 'PDF Security',
        'Image Tools', 'Social Media & Video', 'YouTube Tools', 'AI & Writing', 'Web & SEO', 'Developer Tools',
        'Text & File Tools', 'Converters', 'Calculators', 'Other', 'Blog & Deep Guides', 'Site Pages'
    ];
    
    const sitePages = [
        { id: 'about-us', label: 'About ToolsCopilot', path: '/about-us' },
        { id: 'contact-us', label: 'Contact Support', path: '/contact-us' },
        { id: 'privacy-policy', label: 'Privacy Policy', path: '/privacy-policy' },
        { id: 'terms-of-service', label: 'Terms of Service', path: '/terms-of-service' },
        { id: 'blog', label: 'Blog & Deep Tutorials', path: '/blog' },
        { id: 'sitemap', label: 'HTML Sitemap Directory', path: '/sitemap' }
    ];

    const blogPages = BLOG_POSTS.map(post => ({
        id: post.slug,
        label: post.title,
        path: `/blog/${post.slug}`
    }));

    const categorizedTools = categoryOrder.reduce((acc, category) => {
        if (category === 'Site Pages') {
            acc[category] = sitePages;
        } else if (category === 'Blog & Deep Guides') {
            acc[category] = blogPages;
        } else {
            const toolsInCategory = allTools.filter(tool => tool.category === category);
            if (toolsInCategory.length > 0) {
                // Deduplicate by ID
                const uniqueTools = Array.from(new Map(toolsInCategory.map(t => [t.id, t])).values());
                acc[category] = uniqueTools.sort((a, b) => a.label.localeCompare(b.label));
            }
        }
        return acc;
    }, {} as Record<string, (ToolInfo | PdfImageModeInfo | { path: string; id: string; label: string; })[]>);

    const handleNavigate = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
        e.preventDefault();
        navigateTo(path);
    };

    return (
        <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-12 text-left">
                {categoryOrder.map(category => (
                    categorizedTools[category] && categorizedTools[category].length > 0 && (
                        <div key={category} className="space-y-3">
                            <h2 className="text-lg font-black text-slate-900 dark:text-slate-100 border-b-2 border-cyan-500 pb-2">
                                {category} ({categorizedTools[category].length})
                            </h2>
                            <ul className="space-y-2">
                                {categorizedTools[category].map(item => {
                                    const path = (item as {path?: string}).path || `/tool/${item.id}`;
                                    return (
                                        <li key={item.id}>
                                            <a
                                                href={path}
                                                onClick={(e) => handleNavigate(e, path)}
                                                className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 hover:text-cyan-600 dark:hover:text-cyan-400 hover:underline transition-colors block leading-tight font-medium"
                                            >
                                                {item.label}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )
                ))}
            </div>
        </div>
    );
};